import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import styles from "./Checkout.module.css";
import Api from "../../utils/Api/Api";
import Helper from "../../utils/Helper";
import UTMTracker from "../../utils/UTMTracker";
import { money, maskPhone } from "../../utils/format";
import { isInr, roundMinor } from "../../utils/currency";
import { COINS_REDEMPTION_ENABLED } from "../../constants";
import { MOCK_PAYMENTS, rememberOrderAmount } from "../../utils/Api/mockPayments";
import { useAuthGate, hasSession } from "../../components/AuthModal/AuthGate";
import { ErrorFallback, createErrorDetails } from "../../components/Error/Error";
import { TopBar } from "../../components/TopBar/TopBar";
import { CtaBar } from "../../components/CtaBar/CtaBar";
import { track, identify, EVENTS, maskErrorText } from "../../utils/analyticsService";
import { PhonePeDrawer } from "./PhonePeDrawer";
import { useAdCoupons, adCouponFor } from "../../utils/adCoupon";
import { getMarket, isIndia, marketInfo } from "../../utils/market";
import { dialCodeFor } from "../../utils/dialCodes";
import { giftDraftFor, giftOrderBody, rememberGiftOrder, clearGiftDraft, firstName } from "../../utils/gift";
import coinLogo from "../../asset/icons/fleek-coin-logo.png";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";

import "react-toastify/dist/ReactToastify.css";

const CHECKOUT_SNAPSHOT_KEY = "fleek_checkout_snapshot";
const readCheckoutSnapshot = () => {
  try {
    return JSON.parse(localStorage.getItem(CHECKOUT_SNAPSHOT_KEY) || "null");
  } catch {
    return null;
  }
};
const writeCheckoutSnapshot = (snap) => {
  try {
    localStorage.setItem(CHECKOUT_SNAPSHOT_KEY, JSON.stringify(snap));
  } catch {
    /* storage full or blocked: the timed-out state just shows the empty card */
  }
};

const INDIAN_PHONE_LENGTH = 10;
const COUNTRY_OPTIONS = [
  { code: "+1", name: "United States (US)", market: "US" },
  { code: "+971", name: "UAE (AE)", market: "AE" },
  { code: "+965", name: "Kuwait (KW)", market: "KW" },
  { code: "+966", name: "Saudi Arabia (SA)", market: "SA" },
  { code: "+968", name: "Oman (OM)", market: "OM" },
  { code: "+973", name: "Bahrain (BH)", market: "BH" },
  { code: "+974", name: "Qatar (QA)", market: "QA" },
];

// The dial code a store's checkout starts on: the store's country, else blank.
const dialFor = (market) => COUNTRY_OPTIONS.find((c) => c.market === market)?.code || dialCodeFor(market);

// The picker's countries: the listed stores, plus this store's own country.
const countryOptions = (market) => {
  if (COUNTRY_OPTIONS.some((c) => c.market === market)) return COUNTRY_OPTIONS;
  const code = dialCodeFor(market);
  const own = code ? [{ code, name: `${marketInfo(market).label} (${market})`, market }] : [{ code: "", name: "Country code", market: "" }];
  return [...own, ...COUNTRY_OPTIONS.filter((c) => c.code !== code)];
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/* A bundle checkout answers with totals, folded into the single cart row the
   rest of the screen expects. */
const bundleRow = (totals, bundle, bundleId) => ({
  subscription_plan: { service_name: "", plan_name: bundle?.name || "", image_url: FleekIcon, bundle_id: bundleId },
  actual_discounted_cost: totals.final_payment_amount?.coin_and_no_money,
  cost: totals.final_payment_amount?.no_coin_and_no_money,
  mrp_price: bundle?.mrp_price,
  coin_value_reserved: totals.coin_value_reserved,
});

/* Money for the summary and the bar. A coupon on a non-bundle order replaces
   every figure, because the server recalculates each line. */
const totalsFor = ({ row, discount, isBundle }) => {
  if (discount && !isBundle) {
    const withCoins = num(discount.final_payment_amount?.coin_and_no_money);
    const withoutCoins = num(discount.final_payment_amount?.no_coin_and_no_money);
    return {
      mrp: num(discount.total_cost),
      price: num(discount.total_cost_calculated),
      coupon: num(discount.coupon_code_value),
      coinsMax: Math.max(0, withoutCoins - withCoins),
      withCoins,
      withoutCoins,
    };
  }
  const price = num(discount?.final_payment_amount?.no_coin_and_no_money ?? row.cost);
  const withCoins = num(discount?.final_payment_amount?.coin_and_no_money ?? row.actual_discounted_cost);
  const coinsMax = num(row.coin_value_reserved);
  return {
    mrp: num(row.mrp_price),
    price,
    coupon: num(discount?.coupon_code_value),
    coinsMax,
    withCoins: coinsMax > 0 ? withCoins : price,
    withoutCoins: price,
  };
};

const couponPayload = (row, extra = {}) => ({
  subscription_plan_id: row?.subscription_plan?.subscription_plan_id,
  bundle_id: row?.subscription_plan?.bundle_id,
  type: row?.subscription_plan?.bundle_id ? "bundle" : "buy_once",
  ...extra,
});

const AlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

/**
 * Checkout, rebuilt from design/Purchase Flow.dc.html (isCheckout block).
 * Product, delivery details, inline coupon, coins slider, order summary, and
 * the pay bar. Payment routing follows the V4 contract: nothing owed completes
 * at once, PhonePe opens the in-app sheet, any other gateway redirects.
 */
export const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { requireAuth } = useAuthGate();
  const isMobile = Helper.isMobile();
  const bundleId = localStorage.getItem("bundleId");

  const paymentFailed = searchParams.get("status") === "failed";
  const failureReason = searchParams.get("reason");

  const [loading, setLoading] = useState(true);
  const [errorDetails, setErrorDetails] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [showFailed, setShowFailed] = useState(paymentFailed);

  const [row, setRow] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [user, setUser] = useState({ email: "", phone: "" });
  const [editingDelivery, setEditingDelivery] = useState(false);
  const [country, setCountry] = useState(() => dialFor(getMarket()));

  const [coupon, setCoupon] = useState(null); // { code, id, saved, discount }
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Pinned off while COINS_REDEMPTION_ENABLED is false; see constants.js.
  const [coinsOn, setCoinsOn] = useState(COINS_REDEMPTION_ENABLED);
  const [sheet, setSheet] = useState(null);
  // What the buyer wrote on the plan page when this checkout is a gift.
  const [gift, setGift] = useState(null);

  const signedIn = hasSession();

  /* ---- Load ---- */

  useEffect(() => {
    if (!signedIn) {
      // "We kept your plan and coupon": the last signed-in checkout is
      // snapshotted so the timed-out state still shows it, as designed.
      const snap = readCheckoutSnapshot();
      if (snap?.row && (snap.bundleId || null) === (bundleId || null)) {
        setRow(snap.row);
        if (!snap.bundleId) setGift(giftDraftFor(snap.row?.subscription_plan?.subscription_plan_id));
        if (snap.user) setUser(snap.user);
        if (snap.coupon) setCoupon(snap.coupon);
      }
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const calls = [Api.GetFleekCoins().catch(() => null), Api.GetUserDetails()];
        calls.push(bundleId ? Api.BundleCheckout({ bundle: bundleId }) : Api.GetCartItems());
        if (bundleId) calls.push(Api.BundleDetails({ params: { bundle_id: bundleId } }));
        // Outside India the cart's own quote is the total: it carries the
        // service fee (backend marketplace.service_fee), which the cart rows
        // leave out in stores where the fee is a checkout line.
        const quoteCall = !bundleId && !isIndia()
          ? Promise.resolve().then(() => Api.GetOrderAmount({})).catch(() => null)
          : Promise.resolve(null);
        const [[coinsRes, profileRes, cartRes, bundleRes], quoteRes] = await Promise.all([Promise.all(calls), quoteCall]);
        if (cancelled) return;
        if (quoteRes?.data?.final_payment_amount) {
          setCoupon((c) => c || { code: null, id: null, saved: 0, discount: quoteRes.data });
        }

        const profile = profileRes.data || {};
        const cart = cartRes.data;
        const bundle = bundleRes?.data?.[0]?.bundle;
        const first = bundle ? bundleRow(cart, bundle, bundleId) : Array.isArray(cart) ? cart[0] : null;

        if (profile.user_id) identify(profile.user_id);
        // A gift draft applies only to the one plan it was written for.
        const giftDraft = bundle ? null : giftDraftFor(first?.subscription_plan?.subscription_plan_id);
        setGift(giftDraft);
        if (first) {
          const fp = first.subscription_plan || {};
          track(EVENTS.CHECKOUT_STARTED, {
            is_gift: !!giftDraft,
            plan_id: fp.subscription_plan_id ?? null,
            bundle_id: bundleId || null,
            plan_name: fp.plan_name || null,
            brand: fp.service_name || null,
            amount: num(first.cost),
            currency: String(fp.currency_type || bundle?.currency_type || "inr").toUpperCase(),
            item_count: Array.isArray(cart) ? cart.length : 1,
          });
        }

        setRow(first || null);
        setWallet(coinsRes?.data || null);
        setUser({ email: profile.email || "", phone: profile.phone_number || "" });
        setEditingDelivery(!profile.email);

        if (first) {
          Api.GetAvailableCoupon(couponPayload(first))
            .then(({ data }) => {
              if (!cancelled) setSuggestions((Array.isArray(data) ? data : []).filter((c) => c.is_valid));
            })
            .catch(() => {});
        }

      } catch (err) {
        if (cancelled) return;
        console.error("Checkout load failed:", err);
        setErrorDetails(
          createErrorDetails(err, {
            componentName: "Checkout",
            errorContext: "checkout_data_fetch",
            apiCallsAttempted: ["GetFleekCoins", "GetUserDetails", bundleId ? "BundleCheckout" : "GetCartItems"],
            additionalContext: { bundle_id: bundleId, payment_status: searchParams.get("status") },
          })
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (signedIn && row) writeCheckoutSnapshot({ row, user, coupon, bundleId: bundleId || null });
  }, [signedIn, row, user, coupon, bundleId]);

  /* ---- Derived ---- */

  // The order's own currency, as the API priced it: "inr", "usd", or a
  // store's own ("eur", "gbp", "jpy"). Anything but rupees is an order outside
  // India: card through Stripe, no coins, coupons or GST.
  const rawCurrency = row?.subscription_plan?.currency_type || localStorage.getItem("current_currency_type") || "inr";
  const currencyType = String(rawCurrency).toLowerCase();
  const isAbroad = !isInr(currencyType);
  const plan = row?.subscription_plan || {};

  const totals = useMemo(() => totalsFor({ row: row || {}, discount: coupon?.discount, isBundle: !!bundleId }), [row, coupon, bundleId]);

  // Coins cover a fixed rupee amount; the backend applies all of it or none.
  const coinsMax = totals.coinsMax;
  // Coins are rupees: never on an order in any other currency. Other
  // currencies keep their decimals (cents; none for yen; three for dinars).
  const coinsApply = coinsOn && !isAbroad;
  const coinsUsed = coinsApply ? coinsMax : 0;
  const payable = isAbroad
    ? Math.max(0, roundMinor(totals.withoutCoins, currencyType))
    : Math.max(0, coinsApply ? Math.round(totals.withCoins) : Math.floor(totals.withoutCoins));
  const saved = Math.max(0, totals.mrp - payable);

  const walletRupees = num(isAbroad ? wallet?.coin_value : wallet?.coin_value_in_inr);
  const walletCoins = wallet?.coins ?? wallet?.total_coins ?? wallet?.coin_balance ?? wallet?.balance ?? null;
  const rupeesToCoins = (r) =>
    walletCoins != null && walletRupees > 0 ? Math.round(r * (Number(walletCoins) / walletRupees)) : null;
  const coinsLabel = (r) => {
    const c = rupeesToCoins(r);
    return c != null ? `${c.toLocaleString("en-IN")} coins` : `${money(r, currencyType)} in coins`;
  };
  const coinShortfall = walletRupees > 0 && coinsMax > 0 ? Math.max(0, num(row?.coin_value) - walletRupees) : 0;

  // The prototype's coin copy, driven by real figures. The earn-back clause
  // depends on the coin backend and is added only when it reports one.
  const coinPct = totals.price > 0 && coinsMax > 0 ? Math.round((coinsMax / totals.price) * 100) : 0;
  const coinsEarned = row?.coins_to_earn ?? row?.earn_coins ?? null;
  const earnClause = coinsEarned != null ? ` You'll earn ${Number(coinsEarned).toLocaleString("en-IN")} coins back on this order.` : "";
  const coinRuleText =
    coinsMax <= 0
      ? "Coins can't be applied to this plan."
      : coinShortfall > 0
        ? `Coins always take ${coinPct}% off the listed price — ${money(coinsMax + coinShortfall, currencyType)} on this plan. You only hold ${coinsLabel(walletRupees)}, so ${money(coinsMax, currencyType)} comes off and you pay ${money(payable, currencyType)}.${earnClause}`
        : `Coins always take ${coinPct}% off the listed price — ${money(totals.price, currencyType)} down to ${money(totals.price - coinsMax, currencyType)}.${earnClause}`;
  const coinsMaxLabel =
    coinShortfall > 0
      ? `Capped at your balance of ${coinsLabel(walletRupees)} · ${coinPct}% of list is ${coinsLabel(coinsMax + coinShortfall)}`
      : `Max ${coinsLabel(coinsMax)} on this plan · ${coinPct}% of list`;

  // A gift carries no phone number (it activates on whoever claims it), so
  // only the receipt email is needed.
  const canPay = isAbroad || gift
    ? (user.email?.length || 0) > 0
    : (user.email?.length || 0) > 0 && user.phone?.length === INDIAN_PHONE_LENGTH;

  if (MOCK_PAYMENTS) rememberOrderAmount(payable);

  /* ---- Coupon ---- */

  const applyCoupon = useCallback(
    async (codeArg) => {
      const code = String(codeArg ?? couponInput).trim().toUpperCase();
      if (!code) {
        setCouponError("Enter a code first.");
        return;
      }
      if (couponBusy) return;
      setCouponBusy(true);
      setCouponError("");
      try {
        const { data } = await Api.ValidateCoupon(couponPayload(row, { coupon_code: code }));
        if (data?.error || !data?.is_valid) {
          track(EVENTS.COUPON_REJECTED, { reason: data?.error ? "invalid" : "not_valid_for_plan", plan_id: row?.subscription_plan?.subscription_plan_id ?? null, bundle_id: bundleId || null });
          setCouponError(data?.error || `This code isn't valid on ${plan.service_name || "this"} plans.`);
          return;
        }
        const { data: discount } = await Api[bundleId ? "BundleCheckout" : "GetOrderAmount"]({
          coupon_code: data.id,
          bundle: bundleId || undefined,
        });
        setCoupon({ code, id: data.id, saved: num(data.final_discount_amount), discount });
        setCouponInput("");
        track(EVENTS.COUPON_APPLIED, {
          discount_amount: num(data.final_discount_amount),
          currency: String(currencyType).toUpperCase(),
          plan_id: row?.subscription_plan?.subscription_plan_id ?? null,
          bundle_id: bundleId || null,
          from_suggestion: codeArg != null,
        });
      } catch {
        track(EVENTS.COUPON_REJECTED, { reason: "network_error", plan_id: row?.subscription_plan?.subscription_plan_id ?? null, bundle_id: bundleId || null });
        setCouponError("Couldn't check that code. Try again.");
      } finally {
        setCouponBusy(false);
      }
    },
    [couponInput, couponBusy, row, plan.service_name, bundleId, currencyType]
  );

  // An ad link's code (utils/adCoupon.js): a label on today's price, never a
  // discount. It shows only while no real coupon is on the order.
  const adConfig = useAdCoupons();
  const adCoupon =
    !bundleId && !coupon?.code && adConfig
      ? adCouponFor({
          config: adConfig.config,
          code: adConfig.code,
          planId: plan.subscription_plan_id,
          mrp: totals.mrp,
          price: totals.price,
          currency: currencyType,
        })
      : null;
  const adShown = useRef(null);
  useEffect(() => {
    if (!adCoupon || adShown.current === adCoupon.code) return;
    adShown.current = adCoupon.code;
    track(EVENTS.COUPON_APPLIED, {
      discount_amount: adCoupon.saving,
      currency: "INR",
      plan_id: plan.subscription_plan_id ?? null,
      bundle_id: null,
      from_suggestion: false,
      from_link: true,
      display_only: true,
    });
  }, [adCoupon, plan.subscription_plan_id]);

  const removeCoupon = useCallback(async () => {
    setCoupon(null);
    try {
      const { data } = await Api[bundleId ? "BundleCheckout" : "GetOrderAmount"](bundleId ? { bundle: bundleId } : {});
      setCoupon(data ? { code: null, id: null, saved: 0, discount: data } : null);
    } catch {
      /* the row's own figures still stand */
    }
  }, [bundleId]);

  /* ---- Pay ---- */

  const pay = useCallback(async () => {
    if (!canPay || placing || !row) return;
    setPlacing(true);
    try {
      const payload = {
        screen_type: bundleId ? "bundle" : "buy_once",
        bundle: bundleId,
        use_coins: COINS_REDEMPTION_ENABLED && coinsOn && !isAbroad,
        os: "web",
        email: user.email,
        discount_coupon_id: coupon?.id || undefined,
        buy_now: bundleId ? true : undefined,
      };
      // Outside India the phone is optional (Google sign-in has none).
      if (isAbroad) {
        if (user.phone) payload.details = { user_entered_phone_number: `${country}${user.phone}` };
      } else payload.phone_number = user.phone;

      const body = gift ? giftOrderBody(payload, gift) : payload;
      const tagged = UTMTracker.addUTMNotesToPayload(body);
      // The ad's code rides with the order for attribution only (no discount).
      if (adCoupon) {
        tagged.metadata = {
          ...(tagged.metadata || {}),
          attribution: { ...(tagged.metadata?.attribution || {}), ad_code: adCoupon.code },
        };
      }
      const placed = await Api.PlaceOrder(tagged);
      if (!placed?.data) {
        track(EVENTS.ERROR_SHOWN, {
          context: "order_create",
          reason: placed?.response?.status ? `http_${placed.response.status}` : "no_response",
          message: maskErrorText(placed?.response?.data?.error),
          plan_id: row?.subscription_plan?.subscription_plan_id ?? null,
        });
        toast(placed?.response?.data?.error || "Something went wrong! Please try later.");
        setPlacing(false);
        return;
      }
      const order = placed.data;
      // The success screen shows the gift, not a receipt, for this order.
      if (gift) rememberGiftOrder(order.id);

      const { data: payment } = await Api.GetPaymentLinkV4({
        type: "MKP",
        is_fleek_money_used: false,
        // Rupee orders pay as before; any other currency is Stripe Checkout
        // (payments/v4 refuses it on any other gateway). The order's own
        // currency, exactly as the API gave it: the backend refuses a mismatch.
        currency: String(rawCurrency).toUpperCase(),
        new_placed_order_id: order.id,
        os: "web",
        // "phonepe_custom_main" is PhonePe's Custom Checkout flow (UPI intent on
        // phones, UPI QR on desktop) on the main merchant ID - subscriptions.
        // "phonepe_custom" is the same flow on the gift-card merchant and is
        // deliberately not used here.
        preferred_gateway: isAbroad ? "stripe_checkout" : "phonepe_custom_main",
        return_url: `${process.env.REACT_APP_PAYMENT_URL}/order-confirmation?order_id=${order.id}`,
        notes: { customer_note: "Order from web checkout", device_type: isMobile ? "mobile" : "desktop", user_agent: navigator.userAgent },
      });

      // One line, so a support screenshot of the console says which way the
      // backend routed this order.
      console.info(
        "[checkout] payment route:",
        payment?.is_purchase_done ? "done" : payment?.payment_options ? "in-app sheet" : payment?.payment_url ? "redirect" : "none",
        "gateway=" + (payment?.gateway || "-"),
        "order=" + order.id
      );

      const paymentProps = {
        order_id: order.id,
        plan_id: plan.subscription_plan_id ?? null,
        bundle_id: bundleId || null,
        amount: payable,
        currency: String(currencyType).toUpperCase(),
        gateway: payment?.gateway || null,
        coins_used: coinsUsed > 0,
        coupon_used: !!coupon?.id,
      };
      if (payment?.is_purchase_done) {
        track(EVENTS.PAYMENT_INITIATED, { ...paymentProps, method: "nothing_owed" });
        navigate(`/order?order_id=${payment.order_id}`);
        return;
      }
      const upiOptions = payment?.payment_options || {};
      const hasUpi = !!(upiOptions.qr_data || upiOptions.intent_url || upiOptions.upi_qr_base64 || upiOptions.upi_intent_url);
      if (hasUpi && String(payment.gateway || "").startsWith("phonepe")) {
        track(EVENTS.PAYMENT_INITIATED, { ...paymentProps, method: "upi_sheet" });
        setSheet({ orderId: payment.order_id, paymentId: payment.payment_id, options: payment.payment_options });
        setPlacing(false);
        return;
      }
      if (payment?.payment_url) {
        track(EVENTS.PAYMENT_INITIATED, { ...paymentProps, method: "redirect" });
        window.location.href = payment.payment_url;
        return;
      }
      console.error("Payment response carried no usable option:", payment);
      track(EVENTS.ERROR_SHOWN, { context: "payment_link", reason: "no_payment_option" });
      toast("Unable to process payment. Please try again.");
      setPlacing(false);
    } catch (err) {
      console.error("Payment processing error:", err);
      track(EVENTS.ERROR_SHOWN, { context: "payment_link", reason: "exception" });
      toast("An error occurred. Please try again.");
      setPlacing(false);
    }
  }, [canPay, placing, row, bundleId, coinsOn, user, coupon, adCoupon, isAbroad, rawCurrency, country, isMobile, navigate, plan.subscription_plan_id, payable, currencyType, coinsUsed, gift]);

  const dismissFailed = () => {
    setShowFailed(false);
    const url = new URL(window.location.href);
    url.search = "";
    window.history.replaceState({}, "", url);
  };

  /* ---- Render ---- */

  if (errorDetails) return <ErrorFallback errorDetails={errorDetails} />;

  // Outside India: the service fee, from the quote (backend
  // marketplace.service_fee). The total already includes it; these lines say so.
  const fee = coupon?.discount?.service_fee && num(coupon.discount.service_fee.amount) > 0 ? coupon.discount.service_fee : null;
  const summaryRows = [
    ...(fee && totals.mrp <= num(fee.item_amount)
      ? []
      : [{ label: "MRP", value: money(totals.mrp, currencyType), weight: "var(--fw-medium)", color: "var(--text-subtle)" }]),
    // An ad visitor sees today's price as MRP less the ad's code; the
    // figures, and what is charged, are the same either way.
    adCoupon
      ? { label: `Coupon ${adCoupon.code} applied`, value: `−${money(adCoupon.saving, currencyType)}`, weight: "var(--fw-semibold)", color: "var(--teal-600)" }
      : fee
        ? { label: "Card value", value: money(fee.item_amount, currencyType), weight: "var(--fw-bold)", color: "var(--text-strong)" }
        : { label: "Fleek price", value: money(totals.price, currencyType), weight: "var(--fw-bold)", color: "var(--text-strong)" },
    ...(fee
      ? [{
          label: "Service fee",
          value: money(fee.amount, currencyType),
          weight: "var(--fw-medium)",
          color: "var(--text-subtle)",
          hint: "Covers card processing. The same for every payment method.",
        }]
      : []),
    ...(coupon?.code && totals.coupon > 0
      ? [{ label: `Coupon ${coupon.code}`, value: `−${money(totals.coupon, currencyType)}`, weight: "var(--fw-semibold)", color: "var(--teal-600)" }]
      : []),
    ...(coinsUsed > 0
      ? [{ label: "Fleek coins applied", value: `−${money(coinsUsed, currencyType)}`, weight: "var(--fw-semibold)", color: "#a9760b" }]
      : []),
    // GST is India's; orders in any other currency show no tax line.
    ...(isAbroad ? [] : [{ label: "GST", value: "Included", weight: "var(--fw-medium)", color: "var(--text-muted)" }]),
  ];

  return (
    <div className={styles.page}>
      <ToastContainer />
      <TopBar title="Checkout" backTo="/cart" />
      <PhonePeDrawer
        visible={!!sheet}
        onClose={() => {
          track(EVENTS.PAYMENT_ABANDONED, { order_id: sheet?.orderId ?? null, stage: "upi_sheet" });
          setSheet(null);
        }}
        orderId={sheet?.orderId}
        paymentOptions={sheet?.options}
        paymentId={sheet?.paymentId}
        onSuccess={() => {
          const id = sheet?.orderId;
          setSheet(null);
          navigate(`/order?order_id=${id}`);
        }}
      />

      <div className={styles.shell}>
        {!signedIn && (
          <div className={styles.alert}>
            <span className={styles.alertIcon}><AlertIcon /></span>
            <div className={styles.alertText}>
              <div className={styles.alertTitle}>Your session timed out</div>
              <p className={styles.alertBody}>
                {isIndia()
                  ? "We kept your plan and coupon. Verify your number again to finish paying."
                  : "We kept your plan. Sign in again to finish paying."}
              </p>
              <button type="button" className={styles.alertButton} onClick={() => requireAuth(() => window.location.reload(), isIndia() ? "Verify your number to finish paying." : "Sign in to finish paying.", { context: "checkout_session" })}>
                {isIndia() ? "Verify number" : "Sign in"}
              </button>
            </div>
          </div>
        )}

        {showFailed && (
          <div className={`${styles.alert} ${styles.alertDanger}`} role="alert">
            <span className={styles.alertIcon}><AlertIcon /></span>
            <div className={styles.alertText}>
              <div className={styles.alertTitle}>Payment didn't go through</div>
              <p className={`${styles.alertBody} ${styles.alertBodyOnly}`}>
                {failureReason === "missing_order_id"
                  ? "We couldn't verify that payment. Nothing was charged — try again."
                  : "Your bank declined the request. Nothing was charged — if you see a debit it'll reverse within 3 working days."}
              </p>
            </div>
            <button type="button" className={styles.linkButton} onClick={dismissFailed} aria-label="Dismiss">✕</button>
          </div>
        )}

        {loading ? (
          <>
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
          </>
        ) : !row ? (
          <div className={`${styles.card} ${styles.cardFirst}`}>
            <div className={styles.brand}>Your cart is empty</div>
            <p className={styles.plan}>Add a plan and come back to pay.</p>
          </div>
        ) : (
          <>
            {/* ---- Gift ---- */}
            {gift && (
              <section className={`${styles.card} ${styles.cardFirst} ${styles.giftCard}`}>
                <div className={styles.cardHead}>
                  <span className={`${styles.eyebrow} ${styles.giftEyebrow}`}>A gift</span>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => {
                      clearGiftDraft();
                      setGift(null);
                    }}
                  >
                    Not a gift
                  </button>
                </div>
                <div className={styles.giftFor}>
                  {firstName(gift.recipient_name) ? `For ${gift.recipient_name}` : "For someone special"}
                </div>
                {gift.message && <p className={styles.giftMessage}>“{gift.message}”</p>}
                <div className={styles.giftFrom}>From {gift.sender_name}</div>
                <p className={styles.giftHint}>After you pay, you'll get a link to send them. Whoever claims it gets the plan.</p>
              </section>
            )}

            {/* ---- Product ---- */}
            <section className={`${styles.card} ${gift ? "" : styles.cardFirst}`}>
              <div className={styles.product}>
                <img className={styles.logo} src={plan.image_url || FleekIcon} alt="" onError={(e) => { e.currentTarget.src = FleekIcon; }} />
                <div className={styles.productText}>
                  <div className={styles.brand}>{plan.service_name || row.product_name}</div>
                  <div className={styles.plan}>{plan.plan_name}</div>
                </div>
                <button
                  type="button"
                  className={`${styles.linkButton} ${styles.change}`}
                  onClick={() => navigate(plan.vendor_name ? `/brand/${plan.vendor_name}` : "/marketplace")}
                >
                  Change
                </button>
              </div>
            </section>

            {/* ---- Delivered to ---- */}
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.eyebrow}>{gift ? "Receipt to" : "Delivered to"}</span>
                <button type="button" className={styles.linkButton} onClick={() => setEditingDelivery((e) => !e)}>
                  {editingDelivery ? "Done" : "Edit"}
                </button>
              </div>
              {editingDelivery ? (
                <div className={styles.fields}>
                  {!gift && (
                    <div className={styles.field}>
                      {isAbroad ? (
                        <select className={styles.countrySelect} value={country} onChange={(e) => setCountry(e.target.value)} aria-label="Country code">
                          {countryOptions(getMarket()).map((c) => (
                            <option key={c.code} value={c.code}>{c.name} {c.code}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={styles.dial}>+91</span>
                    )}
                    <input
                      className={styles.input}
                      type="number"
                      name="phone"
                      placeholder="Phone number"
                      value={user.phone || ""}
                      disabled={!isAbroad}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!isAbroad && v.length > INDIAN_PHONE_LENGTH) return;
                        setUser((u) => ({ ...u, phone: v }));
                      }}
                      aria-label="Phone number"
                    />
                  </div>
                  )}
                  <div className={styles.field}>
                    <input
                      className={styles.input}
                      type="email"
                      name="email"
                      placeholder={gift ? "Email for your receipt" : "Email for your licence key"}
                      value={user.email || ""}
                      onChange={(e) => setUser((u) => ({ ...u, email: e.target.value }))}
                      aria-label="Email"
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.delivery}>
                  {/* The phone is optional outside India: no number, no line (not a bare "+1"). */}
                  {!gift && user.phone && (
                    <span className={styles.deliveryPhone}>{isAbroad ? `${country} ${user.phone}` : maskPhone(user.phone)}</span>
                  )}
                  <span className={styles.deliveryEmail}>{user.email}</span>
                </div>
              )}
              <span className={styles.deliveryHint}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 5h16v14H4z" />
                  <path d="M4 7l8 6 8-6" />
                </svg>
                {gift
                  ? "Your receipt comes here. The plan goes to whoever claims your gift link."
                  : isAbroad
                    ? "Your plan details arrive here by email within 24 hours"
                    : "Your code or activation details arrive here and on WhatsApp"}
              </span>
            </section>

            {/* ---- Coupon ---- (rupee orders only: payments/v4 refuses coupons in any other currency) */}
            {!isAbroad && (<>
            <section className={styles.card}>
              <span className={styles.eyebrow}>Coupon</span>
              {adCoupon && (
                <div className={styles.couponApplied} data-testid="ad-coupon">
                  <span className={styles.couponTick} aria-hidden="true">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </span>
                  <span className={styles.couponText}>
                    <span className={styles.couponCode}>{adCoupon.code}</span>
                    <span className={styles.couponOff}>{money(adCoupon.saving, currencyType)} off MRP applied</span>
                  </span>
                </div>
              )}
              {coupon?.code ? (
                <div className={styles.couponApplied}>
                  <span className={styles.couponTick} aria-hidden="true">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </span>
                  <span className={styles.couponText}>
                    <span className={styles.couponCode}>{coupon.code}</span>
                    <span className={styles.couponOff}>{money(coupon.saved || totals.coupon, currencyType)} off applied</span>
                  </span>
                  <button type="button" className={styles.couponRemove} onClick={removeCoupon}>Remove</button>
                </div>
              ) : (
                <div>
                  <div className={styles.couponRow}>
                    <input
                      className={`${styles.couponInput} ${couponError ? styles.couponInputError : ""}`}
                      value={couponInput}
                      onChange={(e) => {
                        setCouponInput(e.target.value);
                        setCouponError("");
                      }}
                      onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                      placeholder="Enter code"
                      aria-label="Coupon code"
                    />
                    <button type="button" className={styles.couponApply} onClick={() => applyCoupon()} disabled={couponBusy}>
                      {couponBusy ? "Checking…" : "Apply"}
                    </button>
                  </div>
                  {couponError && (
                    <div className={styles.couponError} role="alert">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M15 9l-6 6" />
                        <path d="M9 9l6 6" />
                      </svg>
                      <span className={styles.couponErrorText}>{couponError}</span>
                    </div>
                  )}
                  {suggestions.length > 0 && (
                    <div className={styles.chips}>
                      {suggestions.map((c) => (
                        <button key={c.coupon_code} type="button" className={styles.chip} onClick={() => applyCoupon(c.coupon_code)}>
                          {c.coupon_code}
                          {c.description || c.title || c.discount_text ? ` · ${c.description || c.title || c.discount_text}` : ""}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>

            </>)}

            {/* ---- Fleek coins ---- (rupees, India only) */}
            {!isAbroad && (<>
            <section className={styles.card}>
              <div className={styles.coinsHead}>
                <img src={coinLogo} alt="" className={styles.coinsIcon} />
                <span className={styles.coinsTitle}>Fleek coins</span>
                <span className={styles.coinsAvail}>
                  {walletCoins != null ? `${Number(walletCoins).toLocaleString("en-IN")} available` : `${money(walletRupees, currencyType)} available`}
                </span>
              </div>
              <p className={styles.coinsRule}>{coinRuleText}</p>
              <div className={styles.coinsFigures}>
                <span className={styles.coinsOff}>−{money(coinsUsed, currencyType)}</span>
                <span className={styles.coinsApplied}>{coinsLabel(coinsUsed)} applied</span>
              </div>
              <input
                type="range"
                className={styles.slider}
                min={0}
                max={Math.max(coinsMax, 1)}
                step={1}
                value={coinsUsed}
                disabled={!COINS_REDEMPTION_ENABLED || coinsMax <= 0}
                onChange={(e) => {
                  if (!COINS_REDEMPTION_ENABLED) return;
                  const next = Number(e.target.value) >= coinsMax / 2;
                  if (next !== coinsOn) track(EVENTS.COINS_TOGGLED, { enabled: next });
                  setCoinsOn(next);
                }}
                aria-label="Coins to apply"
                aria-valuetext={`${money(coinsUsed, currencyType)} in coins`}
              />
              <div className={styles.sliderLabels}>
                <span className={styles.sliderLabel}>0</span>
                <span className={styles.sliderLabel}>{coinsMaxLabel}</span>
              </div>
              {COINS_REDEMPTION_ENABLED && coinShortfall > 0 && (
                <div className={styles.coinsShort}>
                  <span className={styles.coinsShortIcon}><AlertIcon /></span>
                  <span className={styles.coinsShortText}>
                    You're {coinsLabel(coinShortfall).replace(" in coins", "")} short, so this order lands at {money(payable, currencyType)} instead.
                  </span>
                </div>
              )}
            </section>

            </>)}

            {/* ---- Order summary ---- */}
            <section className={styles.card}>
              <span className={styles.eyebrow}>Order summary</span>
              <div className={styles.summary}>
                {summaryRows.map((r) => (
                  <div className={styles.summaryRow} key={r.label}>
                    <span className={styles.summaryLabel} style={{ fontWeight: r.weight, color: r.color }} title={r.hint || undefined}>
                      {r.label}
                      {r.hint ? <span className={styles.summaryHint}>{r.hint}</span> : null}
                    </span>
                    <span className={styles.summaryValue} style={{ color: r.color }}>{r.value}</span>
                  </div>
                ))}
              </div>
              <div className={styles.toPay}>
                <span className={styles.toPayLabel}>To pay</span>
                <span className={styles.toPayValue}>{money(payable, currencyType)}</span>
              </div>
              {coinsUsed > 0 && (
                <div className={styles.coinsLine}>
                  <img src={coinLogo} alt="" className={styles.coinsLineIcon} />
                  <span className={styles.coinsLineText}>{coinsLabel(coinsUsed)} took {money(coinsUsed, currencyType)} off</span>
                </div>
              )}
              {saved > 0 && (
                <div className={styles.saving}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#04566B" strokeWidth="2.4" strokeLinecap="round">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                  <span className={styles.savingText}>You're saving {money(saved, currencyType)} on this order</span>
                </div>
              )}
            </section>

            <CtaBar.Spacer />
            <CtaBar
              label="To pay"
              amount={money(payable, currencyType)}
              strike={totals.mrp > 0 ? money(totals.mrp, currencyType) : ""}
              text={placing ? "Processing…" : `Pay ${money(payable, currencyType)}`}
              onClick={pay}
              disabled={!canPay || placing || !signedIn}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Checkout;
